'use server'

import { apiRequest, ApiResponse } from '../request'
import type {
    WhatsappTemplateApprovalStatus,
    WhatsappTemplateCategory,
} from '../messaging/whatsapp-templates-api'

export type SystemTemplateVersionStatus = 'DRAFT' | 'PUBLISHED' | 'RETIRED'

export interface SystemTemplateVariable {
    index: number
    name: string
    example: string
}

export interface SystemTemplateVersion {
    id: number
    scope: string
    version: number
    name: string
    friendlyName: string
    category: WhatsappTemplateCategory
    language: string
    bodyText: string
    headerText: string | null
    footerText: string | null
    variables: SystemTemplateVariable[]
    status: SystemTemplateVersionStatus
    publishedAt: string | null
    retiredAt: string | null
    createdAt: string
    updatedAt: string
}

export interface SystemTemplateCopyCount {
    scope: string
    version: number | null
    approvalStatus: WhatsappTemplateApprovalStatus
    count: number
}

export interface SystemTemplateOverviewItem {
    scope: string
    label: string
    variables: { index: number; name: string; label: string }[]
    requiresSecondarySegment: string | null
    inContract: boolean
    published: SystemTemplateVersion | null
    draft: SystemTemplateVersion | null
    versions: SystemTemplateVersion[]
    copies: SystemTemplateCopyCount[]
}

export interface SystemTemplateOrganizationStatus {
    templateId: number
    organizationId: number | null
    organizationName: string | null
    version: number | null
    name: string
    approvalStatus: WhatsappTemplateApprovalStatus
    approvalRejectionReason: string | null
    lastSyncedAt: string | null
    hasWhatsappAccount: boolean
}

export interface SystemTemplateScopeDetail {
    scope: string
    versions: SystemTemplateVersion[]
    organizations: SystemTemplateOrganizationStatus[]
}

export interface UpdateSystemTemplateDraftInput {
    friendlyName?: string
    category?: WhatsappTemplateCategory
    bodyText?: string
    headerText?: string | null
    footerText?: string | null
    variables?: { index: number; example: string }[]
}

const BASE = 'master/whatsapp-system-templates'

export async function getSystemTemplatesOverview(): Promise<
    ApiResponse<SystemTemplateOverviewItem[]>
> {
    return apiRequest<SystemTemplateOverviewItem[]>(BASE, {})
}

export async function getSystemTemplateScope(
    scope: string,
): Promise<ApiResponse<SystemTemplateScopeDetail>> {
    return apiRequest<SystemTemplateScopeDetail>(`${BASE}/scopes/${encodeURIComponent(scope)}`, {})
}

export async function createSystemTemplateDraft(
    scope: string,
): Promise<ApiResponse<SystemTemplateVersion>> {
    return apiRequest<SystemTemplateVersion>(
        `${BASE}/scopes/${encodeURIComponent(scope)}/drafts`,
        {},
        'post',
    )
}

export async function updateSystemTemplateDraft(
    id: number,
    data: UpdateSystemTemplateDraftInput,
): Promise<ApiResponse<SystemTemplateVersion>> {
    return apiRequest<SystemTemplateVersion>(`${BASE}/drafts/${id}`, { data }, 'patch')
}

export async function discardSystemTemplateDraft(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`${BASE}/drafts/${id}`, {}, 'delete')
}

export async function publishSystemTemplateDraft(
    id: number,
): Promise<ApiResponse<SystemTemplateVersion>> {
    return apiRequest<SystemTemplateVersion>(`${BASE}/drafts/${id}/publish`, {}, 'post')
}

export async function propagateSystemTemplates(): Promise<ApiResponse<{ queued: boolean }>> {
    return apiRequest<{ queued: boolean }>(`${BASE}/propagate`, {}, 'post')
}

export async function syncSystemTemplatesForOrganization(
    organizationId: number,
): Promise<ApiResponse<{ synced: number }>> {
    return apiRequest<{ synced: number }>(`${BASE}/organizations/${organizationId}/sync`, {}, 'post')
}
